import { hasActiveLiveKitConnection } from "@/lib/livekit-session-guards";
import type { ConnectionState } from "livekit-client";

type ShutdownLogger = {
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
};

type LiveKitRoomForShutdown = {
  state: ConnectionState | null | undefined;
  name?: string;
  localParticipant: {
    identity?: string;
    publishData: (
      data: Uint8Array,
      options: {
        reliable: true;
        topic: "session-control";
      }
    ) => Promise<void>;
  };
};

export type LiveKitSessionPresenceType = "client_backgrounded" | "client_foregrounded";

export async function sendSessionPresenceMessage({
  activeRoom,
  type,
  source,
  logger,
}: {
  activeRoom: LiveKitRoomForShutdown;
  type: LiveKitSessionPresenceType;
  source: string;
  logger: ShutdownLogger;
}): Promise<boolean> {
  if (!hasActiveLiveKitConnection(activeRoom.state)) {
    return false;
  }

  try {
    await activeRoom.localParticipant.publishData(
      new TextEncoder().encode(
        JSON.stringify({
          type,
          source,
          sentAt: new Date().toISOString(),
        })
      ),
      {
        reliable: true,
        topic: "session-control",
      }
    );
    logger.info("Sent LiveKit session presence message", {
      type,
      source,
      roomName: activeRoom.name || null,
    });
    return true;
  } catch (error) {
    logger.warn("Failed to send LiveKit session presence message", {
      type,
      source,
      roomName: activeRoom.name || null,
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
}

export async function sendSessionControlShutdownMessage({
  activeRoom,
  source,
  logger,
}: {
  activeRoom: LiveKitRoomForShutdown;
  source: string;
  logger: ShutdownLogger;
}): Promise<boolean> {
  if (!hasActiveLiveKitConnection(activeRoom.state)) {
    return false;
  }

  try {
    const payload = {
      type: "client_final_disconnect",
      source,
      sentAt: new Date().toISOString(),
    };
    await activeRoom.localParticipant.publishData(
      new TextEncoder().encode(JSON.stringify(payload)),
      {
        reliable: true,
        topic: "session-control",
      }
    );
    logger.info("Sent LiveKit session-control shutdown message", {
      source,
      roomName: activeRoom.name || null,
    });
    return true;
  } catch (error) {
    logger.warn("Failed to send LiveKit session-control shutdown message", {
      source,
      roomName: activeRoom.name || null,
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
}

export async function requestServerSessionShutdown({
  activeRoom,
  source,
  shutdownCapability,
  keepalive,
  timeoutMs,
  logger,
}: {
  activeRoom: LiveKitRoomForShutdown;
  source: string;
  shutdownCapability?: string | null;
  keepalive?: boolean;
  timeoutMs: number;
  logger: ShutdownLogger;
}): Promise<boolean> {
  if (!hasActiveLiveKitConnection(activeRoom.state)) {
    return false;
  }

  const roomName = activeRoom.name || null;
  const participantIdentity = activeRoom.localParticipant.identity || null;
  if (!roomName || !participantIdentity) {
    return false;
  }

  return requestServerSessionShutdownForConnectionDetails({
    roomName,
    participantIdentity,
    source,
    shutdownCapability,
    keepalive,
    timeoutMs,
    logger,
  });
}

export async function requestServerSessionShutdownForConnectionDetails({
  roomName,
  participantIdentity,
  source,
  shutdownCapability,
  keepalive,
  timeoutMs,
  logger,
}: {
  roomName: string;
  participantIdentity: string;
  source: string;
  shutdownCapability?: string | null;
  keepalive?: boolean;
  timeoutMs: number;
  logger: ShutdownLogger;
}): Promise<boolean> {
  if (!roomName || !participantIdentity) {
    return false;
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(new Error(`session_shutdown_timeout_${timeoutMs}ms`)),
    timeoutMs
  );

  try {
    const response = await fetch("/api/livekit/session-shutdown", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        roomName,
        participantIdentity,
        source,
        ...(shutdownCapability ? { shutdownCapability } : {}),
      }),
      keepalive,
      signal: controller.signal,
    });

    if (!response.ok) {
      logger.warn("Server-side LiveKit session shutdown request was rejected", {
        source,
        roomName,
        participantIdentity,
        status: response.status,
      });
      return false;
    }

    logger.info("Requested server-side LiveKit session shutdown", {
      source,
      roomName,
      participantIdentity,
    });
    return true;
  } catch (error) {
    const abortedByTimeout = controller.signal.aborted;
    logger.warn("Failed to request server-side LiveKit session shutdown", {
      source,
      roomName,
      participantIdentity,
      timeoutMs,
      abortedByTimeout,
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  } finally {
    window.clearTimeout(timeoutId);
  }
}
